import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Form, Row, Col, Badge, ProgressBar, Accordion, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import { Get } from '../../utils/httpProvider';
import { MEMBER_TRACKING_API } from '../../utils/apiUrl';
import moment from 'moment';
import SmallBox from '../../components/small-box/SmallBox';
import Plotly from 'plotly.js-basic-dist';
import createPlotlyComponent from 'react-plotly.js/factory';
import locale from 'plotly.js-locales/vi';
import { userManager } from '../../utils/oidc';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import DatePicker from '@app/components/CustomDatePicker';
const Plot = createPlotlyComponent(Plotly);
Plotly.register(locale);
Plotly.setPlotConfig({ locale: 'vi' });

const MemberTracking = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { listAccount } = useSelector((state) => state.account);

    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [apiUserId, setApiUserId] = useState('');
    const [trackingData, setTrackingData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [projectStatusFilter, setProjectStatusFilter] = useState('ALL');
    const currentYear = new Date().getFullYear();
    const defaultFrom = `${currentYear}-01-01`;
    const defaultTo = `${currentYear}-12-31`;
    const [filterFrom, setFilterFrom] = useState(defaultFrom);
    const [filterTo, setFilterTo] = useState(defaultTo);
    const [appliedFrom, setAppliedFrom] = useState(defaultFrom);
    const [appliedTo, setAppliedTo] = useState(defaultTo);

    useEffect(() => {
        userManager.getUser().then(user => {
            setCurrentUser(user);
            const roles = user?.profile?.role || [];
            const hasAdminRole = Array.isArray(roles) ? roles.includes('admin') : roles === 'admin';
            setIsAdmin(hasAdminRole);
        });
    }, []);

    useEffect(() => {
        if (isAdmin) {
            dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        }
    }, [dispatch, isAdmin]);

    useEffect(() => {
        if (apiUserId) {
            fetchTrackingData(apiUserId, appliedFrom, appliedTo);
        } else {
            setTrackingData(null);
        }
    }, [apiUserId]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const urlUserId = queryParams.get('userId');
        if (urlUserId) {
            setSelectedAccountId(urlUserId);
            setApiUserId(urlUserId);
        }
    }, [location.search]);

    const accounts = useMemo(() => {
        if (!isAdmin) {
            const currentUserId = currentUser?.profile?.sub;
            const currentUsername = currentUser?.profile?.preferred_username || currentUser?.profile?.name || '';

            if (!currentUserId) return [];

            return [{
                id: currentUserId,
                fullName: currentUsername || 'Tai khoan hien tai',
                userName: currentUsername,
                roles: []
            }];
        }

        return (listAccount || [])
            .map(item => {
                // Trường hợp 1: Object Account (từ accountPagingAction)
                if (item.roles || item.Roles) {
                    const id = item.id || item.Id;
                    const username = item.username || item.Username;
                    const roles = item.roles || item.Roles || [];
                    const authInfo = item.accountAuthInfo || item.AccountAuthInfo;
                    const lastName = authInfo?.lastName || authInfo?.LastName || '';
                    const middleName = authInfo?.middleName || authInfo?.MiddleName || '';
                    const firstName = authInfo?.firstName || authInfo?.FirstName || '';
                    const fullName = `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ');
                    return {
                        id: id,
                        fullName: fullName || username,
                        userName: username,
                        roles: roles
                    };
                }
                
                // Trường hợp 2: Object AccountAuthInfo (từ accountAuthInfoPagingAction)
                const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
                const id = item.accountId || acc?.id || acc?.Id;
                const username = acc?.username || acc?.Username;
                const roles = acc?.roles || acc?.Roles || [];
                const lastName = item.lastName || '';
                const middleName = item.middleName || '';
                const firstName = item.firstName || '';
                const fullName = `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ');
                return {
                    id: id,
                    fullName: fullName || username,
                    userName: username,
                    roles: roles
                };
            })
            .filter(item => {
                return item.roles && item.roles.some(r => (r.name || r.Name) === 'APP_KPI_C9');
            });
    }, [currentUser, isAdmin, listAccount]);

    const accountOptions = useMemo(() => accounts.map(acc => ({
        value: String(acc.id),
        label: acc.fullName || acc.userName
    })), [accounts]);

    // Auto-select user khi accounts đã load xong (cho nhân viên bình thường)
    useEffect(() => {
        if (!currentUser || selectedAccountId) {
            return;
        }

        if (!isAdmin) {
            const currentUserId = currentUser.profile?.sub || '';
            const currentUsername = currentUser.profile?.preferred_username || currentUser.profile?.name || '';

            if (currentUserId) {
                setSelectedAccountId(currentUserId);
                setApiUserId(currentUserId);
            } else if (currentUsername) {
                setSelectedAccountId(currentUsername);
                setApiUserId(currentUsername);
            }
            return;
        }

        if (currentUser && accounts.length > 0) {
            const oidcUsername = currentUser.profile?.preferred_username || currentUser.profile?.name || '';
            const matchedList = accounts.filter(a => 
                a.userName && oidcUsername && 
                a.userName.toLowerCase() === oidcUsername.toLowerCase()
            );
            console.log('Matched accounts for user:', matchedList);
            
            if (matchedList.length > 0) {
                // Try the last matched account in case there are duplicates and the first one is inactive
                const matched = matchedList[matchedList.length - 1];
                console.log('Auto-selecting account ID:', matched.id, 'with SSO sub:', currentUser.profile.sub);
                
                setSelectedAccountId(matched.id); // Để hiển thị đúng trên Dropdown
                // Dùng thử lại ID nguyên bản thay vì sub xem sao?
                // Vì Admin dùng ID nguyên bản thì được!
                setApiUserId(matched.id); 
            }
        }
    }, [isAdmin, currentUser, accounts, selectedAccountId]);

    const trackingMetrics = useMemo(() => {
        if (!trackingData) return null;
        
        let totalAnnualScore = 0;
        let totalRegisteredScore = 0;
        let totalProjectScore = 0;
        let totalUnassessedScore = 0;
        
        if (trackingData.annualJobRegistrations) {
            trackingData.annualJobRegistrations.forEach(reg => {
                reg.details.forEach(d => {
                    if (d.status !== 3 && d.status !== 4) return;
                    const baseScore = d.currentScore || 0;
                    if (d.evaluationPercent !== null && d.evaluationPercent !== undefined)
                        totalAnnualScore += baseScore * Number(d.evaluationPercent) / 100;
                    else totalUnassessedScore += baseScore;
                    totalRegisteredScore += d.registeredScore || 0;
                });
            });
        }
        
        if (trackingData.activeProjects) {
            trackingData.activeProjects.forEach(p => {
                if (p.memberStatus === 3 || p.memberStatus === 4) {
                    const baseScore = p.memberWeight || 0;
                    if (p.evaluationPercent !== null && p.evaluationPercent !== undefined)
                        totalProjectScore += baseScore * Number(p.evaluationPercent) / 100;
                    else totalUnassessedScore += baseScore;
                }
            });
        }
        
        let progressPercentage = 0;
        if (totalRegisteredScore > 0) {
            progressPercentage = (totalAnnualScore / totalRegisteredScore) * 100;
        }
        
        return {
            totalAnnualScore,
            totalRegisteredScore,
            totalProjectScore,
            totalScore: totalAnnualScore + totalProjectScore,
            totalUnassessedScore,
            projectedScore: totalAnnualScore + totalProjectScore + totalUnassessedScore,
            progressPercentage
        };
    }, [trackingData]);

    const annualStatusCounts = useMemo(() => {
        let todo = 0, inProgress = 0, waiting = 0, done = 0, overdue = 0;
        if (trackingData && trackingData.annualJobRegistrations) {
            trackingData.annualJobRegistrations.forEach(reg => {
                reg.details.forEach(d => {
                    if (d.status === 0) todo++;
                    else if (d.status === 1) inProgress++;
                    else if (d.status === 2) waiting++;
                    else if (d.status === 3) done++;
                    else if (d.status === 4) overdue++;
                });
            });
        }
        return { todo, inProgress, waiting, done, overdue };
    }, [trackingData]);

    const annualPieChartData = useMemo(() => {
        const labels = ['Cần làm', 'Đang làm', 'Chờ duyệt', 'Hoàn thành', 'Quá hạn'];
        const values = [
            annualStatusCounts.todo,
            annualStatusCounts.inProgress,
            annualStatusCounts.waiting,
            annualStatusCounts.done,
            annualStatusCounts.overdue
        ];

        return [{
            values,
            labels,
            type: 'pie',
            hole: 0.45,
            marker: {
                colors: ['#6c757d', '#007bff', '#17a2b8', '#28a745', '#dc3545']
            },
            textinfo: 'value',
            textposition: 'inside',
            automargin: true
        }];
    }, [annualStatusCounts]);

    const annualPieLayout = {
        height: 280,
        margin: { l: 10, r: 10, t: 10, b: 10 },
        showlegend: true,
        legend: { orientation: 'v', x: 1, y: 0.5 }
    };

    const progressBarColor = useMemo(() => {
        if (!trackingMetrics) return 'danger';
        const p = trackingMetrics.progressPercentage;
        if (p >= 80) return 'success';
        if (p >= 50) return 'warning';
        return 'danger';
    }, [trackingMetrics]);

    const fetchTrackingData = async (userId, overrideFrom, overrideTo) => {
        if (!userId && isAdmin) return;

        const fromDateVal = overrideFrom !== undefined ? overrideFrom : filterFrom;
        const toDateVal = overrideTo !== undefined ? overrideTo : filterTo;

        setLoading(true);
        try {
            let endpoint = MEMBER_TRACKING_API.GET_MEMBER_TRACKING;
            if (userId) {
                endpoint = `${endpoint}/${userId}`;
            }
            if (fromDateVal && toDateVal) {
                endpoint += `?fromDate=${encodeURIComponent(fromDateVal)}&toDate=${encodeURIComponent(toDateVal)}`;
            }
            
            console.log('Fetching tracking data from endpoint:', endpoint);
            const res = await Get(endpoint);
            console.log('Tracking API response:', res);
            
            if (res) {
                const rawData = res.data ?? res.Data ?? res;
                const regObj = rawData.LatestAnnualJobRegistration ?? rawData.latestAnnualJobRegistration;
                const activeProjects = rawData.Projects ?? rawData.projects ?? [];
                
                let normalizedRegs = [];
                const activeRegsObj = rawData.ActiveAnnualJobRegistrations ?? rawData.activeAnnualJobRegistrations ?? [];
                const rawRegs = Array.isArray(activeRegsObj) ? activeRegsObj : (activeRegsObj.$values || []);
                const fromMoment = fromDateVal ? moment(fromDateVal).startOf('day') : null;
                const toExclusiveMoment = toDateVal ? moment(toDateVal).add(1, 'day').startOf('day') : null;
                
                // Helper to normalize a single registration
                const normalizeReg = (regObj) => {
                    const rawDetails = regObj.Details ?? regObj.details;
                    const details = (rawDetails && rawDetails.$values ? rawDetails.$values : rawDetails) || [];
                    const registrationStartDate = regObj.StartDate ?? regObj.startDate;
                    const registrationEndDate = regObj.EndDate ?? regObj.endDate;
                    return {
                        id: regObj.Id ?? regObj.id,
                        name: regObj.Name ?? regObj.name,
                        startDate: registrationStartDate,
                        endDate: registrationEndDate,
                        details: details
                            .map(d => ({
                                id: d.Id ?? d.id,
                                annualJobName: d.AnnualJobName ?? d.annualJobName ?? d.JobName ?? d.jobName,
                                annualJobUnit: d.AnnualJobUnit ?? d.annualJobUnit ?? d.Unit ?? d.unit,
                                registeredKPI: d.RegisteredKPI ?? d.registeredKPI,
                                registeredScore: d.RegisteredScore ?? d.registeredScore ?? 0,
                                currentKPI: d.CurrentKPI ?? d.currentKPI,
                                currentScore: d.CurrentScore ?? d.currentScore ?? 0,
                                evaluationPercent: d.EvaluationPercent ?? d.evaluationPercent ?? null,
                                actualScore: d.ActualScore ?? d.actualScore ?? null,
                                status: d.Status ?? d.status ?? 0,
                                endDate: d.EndDate ?? d.endDate,
                                actualEndDate: d.ActualEndDate ?? d.actualEndDate
                            }))
                            .filter(d => {
                                if (!fromMoment || !toExclusiveMoment) return true;

                                if (d.status === 3) {
                                    if (!d.actualEndDate) return false;
                                    const actualEndDate = moment(d.actualEndDate);
                                    return actualEndDate.isSameOrAfter(fromMoment) && actualEndDate.isBefore(toExclusiveMoment);
                                }

                                const effectiveEndDate = moment(d.endDate || registrationEndDate);
                                return moment(registrationStartDate).isBefore(toExclusiveMoment)
                                    && effectiveEndDate.isSameOrAfter(fromMoment);
                            })
                    };
                };

                if (rawRegs.length > 0) {
                    normalizedRegs = rawRegs.map(r => normalizeReg(r)).filter(r => r.details.length > 0);
                } else if (regObj) {
                    const normalizedReg = normalizeReg(regObj);
                    normalizedRegs = normalizedReg.details.length > 0 ? [normalizedReg] : [];
                }
                
                const rawProjList = Array.isArray(activeProjects) ? activeProjects : (activeProjects.$values || []);
                const normalizedProjects = rawProjList.map(p => {
                    const rawMembers = p.Members ?? p.members ?? [];
                    const members = Array.isArray(rawMembers) ? rawMembers : (rawMembers.$values || []);
                    const me = members.find(m => String(m.UserId || m.userId) === String(userId));
                    const memberWeight = me ? (me.MemberWeight ?? me.memberWeight ?? 0) : 0;
                    const evaluationPercent = me ? (me.EvaluationPercent ?? me.evaluationPercent ?? null) : null;
                    const memberStatus = me ? (me.Status ?? me.status ?? 0) : 0;

                    return {
                        id: p.Id ?? p.id,
                        name: p.Name ?? p.name,
                        startDate: p.StartDate ?? p.startDate,
                        endDate: p.EndDate ?? p.endDate,
                        status: p.Status ?? p.status ?? 0,
                        memberWeight: memberWeight,
                        evaluationPercent: evaluationPercent,
                        memberStatus: memberStatus
                    };
                });
                
                setTrackingData({
                    annualJobRegistrations: normalizedRegs,
                    activeProjects: normalizedProjects
                });
                setAppliedFrom(fromDateVal);
                setAppliedTo(toDateVal);
            }
        } catch (error) {
            console.error('Error fetching tracking data:', error);
            setTrackingData(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header>
                    <h3 className="card-title">Theo dõi tiến độ theo cá nhân</h3>
                </Card.Header>
                <Card.Body style={{ minHeight: '600px', paddingBottom: '150px' }}>
                    <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                        <h4 className="mb-0 text-secondary">
                            <i className="fas fa-user-circle me-2"></i>
                            {accounts.find(a => String(a.id) === String(selectedAccountId))?.fullName || (isAdmin ? "Chọn nhân viên..." : "Đang tải dữ liệu...")}
                        </h4>
                        <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                            <div className="text-muted" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>Từ ngày:</div>
                            <div style={{ width: '140px' }}>
                                <DatePicker 
                                    className="form-control form-control-sm w-100"
                                    selected={filterFrom ? new Date(filterFrom) : null}
                                    onChange={(date) => setFilterFrom(date ? moment(date).format('YYYY-MM-DD') : '')}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                />
                            </div>
                            <div className="text-muted" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>Đến ngày:</div>
                            <div style={{ width: '140px' }}>
                                <DatePicker 
                                    className="form-control form-control-sm w-100"
                                    selected={filterTo ? new Date(filterTo) : null}
                                    onChange={(date) => setFilterTo(date ? moment(date).format('YYYY-MM-DD') : '')}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                />
                            </div>
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={loading || !apiUserId}
                                onClick={() => {
                                    if (!filterFrom || !filterTo) {
                                        toast.warning('Vui lòng chọn đầy đủ Từ ngày và Đến ngày');
                                        return;
                                    }
                                    if (filterFrom > filterTo) {
                                        toast.warning('Từ ngày không được lớn hơn Đến ngày');
                                        return;
                                    }
                                    fetchTrackingData(apiUserId, filterFrom, filterTo);
                                }}
                            >
                                <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-filter'} mr-1`}></i>
                                Lọc
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={loading || !apiUserId}
                                onClick={() => {
                                    setFilterFrom(defaultFrom);
                                    setFilterTo(defaultTo);
                                    fetchTrackingData(apiUserId, defaultFrom, defaultTo);
                                }}
                            >
                                <i className="fas fa-times mr-1"></i>
                                Xóa lọc
                            </Button>
                            <div style={{ width: '300px' }}>
                                <Select
                                    value={accountOptions.find(option => option.value === String(selectedAccountId)) || null}
                                    options={accountOptions}
                                    onChange={(option) => {
                                        const accountId = option?.value || '';
                                        setSelectedAccountId(accountId);
                                        setApiUserId(accountId);
                                    }}
                                    placeholder="-- Chọn nhân sự để theo dõi --"
                                    noOptionsMessage={() => 'Không tìm thấy nhân sự'}
                                    isSearchable
                                    isClearable={isAdmin}
                                    isDisabled={!isAdmin}
                                    classNamePrefix="member-select"
                                    styles={{
                                        control: base => ({
                                            ...base,
                                            minHeight: 31,
                                            height: 31
                                        }),
                                        valueContainer: base => ({
                                            ...base,
                                            height: 31,
                                            padding: '0 8px'
                                        }),
                                        input: base => ({
                                            ...base,
                                            margin: 0,
                                            padding: 0
                                        }),
                                        indicatorsContainer: base => ({
                                            ...base,
                                            height: 31
                                        }),
                                        clearIndicator: base => ({
                                            ...base,
                                            padding: 4
                                        }),
                                        dropdownIndicator: base => ({
                                            ...base,
                                            padding: 4
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {!loading && trackingData && trackingMetrics && (
                        <Row className="mb-4">
                            <Col lg={true} sm={6} className="mb-3">
                                <SmallBox type="info" count={trackingMetrics.totalScore.toFixed(2)} title="Điểm thực nhận" des="Các công việc đã đánh giá" />
                            </Col>
                            <Col lg={true} sm={6} className="mb-3">
                                <SmallBox type="secondary" count={trackingMetrics.totalUnassessedScore.toFixed(2)} title="Chưa đánh giá" des="Điểm gốc công việc đã kết thúc" />
                            </Col>
                            <Col lg={true} sm={6} className="mb-3">
                                <SmallBox type="primary" count={trackingMetrics.projectedScore.toFixed(2)} title="KPI dự tính" des="Đã đánh giá + chưa đánh giá" />
                            </Col>
                            <Col lg={true} sm={6} className="mb-3">
                                <SmallBox type="success" count={trackingMetrics.totalAnnualScore.toFixed(2)} title="Điểm Thường Niên" des="Công việc cố định" />
                            </Col>
                            <Col lg={true} sm={6} className="mb-3">
                                <SmallBox type="warning" count={trackingMetrics.totalProjectScore.toFixed(2)} title="Điểm Đột Xuất" des="Dự án phát sinh" />
                            </Col>
                            <Col lg={true} sm={6} className="mb-3">
                                <SmallBox type="primary" count={`${trackingMetrics.progressPercentage.toFixed(1)}%`} title="Tiến Độ KPI" des="So với cam kết" />
                            </Col>
                            <Col lg={true} sm={6} className="mb-3">
                                <SmallBox type="secondary" count={annualStatusCounts.todo} title="Cần làm" des="Công việc chưa bắt đầu" />
                            </Col>
                        </Row>
                    )}

                    {!loading && trackingData && trackingMetrics && (
                        <Row className="mb-4">
                            <Col md={6}>
                                <Card className="h-100 shadow-sm border-0">
                                    <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                                        <h6 className="fw-bold text-dark mb-0">
                                            <i className="fas fa-chart-pie text-success me-2"></i>
                                            Phân bổ Trạng thái Công việc
                                        </h6>
                                    </Card.Header>
                                    <Card.Body className="d-flex justify-content-center align-items-center">
                                        {Object.values(annualStatusCounts).reduce((a, b) => a + b, 0) === 0 ? (
                                            <div className="text-muted text-center py-5">Chưa có công việc nào</div>
                                        ) : (
                                            <Plot
                                                data={annualPieChartData}
                                                layout={annualPieLayout}
                                                useResizeHandler={true}
                                                style={{ width: "100%" }}
                                            />
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col md={6}>
                                <Card className="h-100 shadow-sm border-0">
                                    <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                                        <h6 className="fw-bold text-dark mb-0">
                                            <i className="fas fa-tachometer-alt text-primary me-2"></i>
                                            Tiến độ KPI Thường niên
                                        </h6>
                                    </Card.Header>
                                    <Card.Body className="d-flex flex-column justify-content-center px-5">
                                        <div className="text-center mb-3">
                                            <span style={{ fontSize: '48px', fontWeight: 'bold' }} className={`text-${progressBarColor}`}>
                                                {trackingMetrics.progressPercentage.toFixed(1)}%
                                            </span>
                                        </div>
                                        <ProgressBar 
                                            variant={progressBarColor} 
                                            now={Math.min(100, trackingMetrics.progressPercentage)} 
                                            style={{ height: '25px', borderRadius: '15px' }} 
                                        />
                                        <div className="d-flex justify-content-between mt-3 text-muted" style={{ fontSize: '15px' }}>
                                            <span><strong>{trackingMetrics.totalAnnualScore.toFixed(2)}</strong> điểm quy đổi</span>
                                            <span><strong>{trackingMetrics.totalRegisteredScore.toFixed(2)}</strong> điểm cam kết</span>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                {loading && <div className="text-center py-4">Đang tải dữ liệu...</div>}

                {!loading && !trackingData && apiUserId && (
                    <div className="text-center text-muted py-4">Nhân sự này chưa có Phiếu đăng ký thường niên nào được duyệt!</div>
                )}

                {!loading && trackingData && (
                    <>
                        {trackingData.annualJobRegistrations && trackingData.annualJobRegistrations.length > 0 ? (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0 fw-bold">Danh sách Phiếu đăng ký thường niên</h6>
                                    <div className="d-flex align-items-center">
                                        <span className="me-2 text-muted small whitespace-nowrap" style={{ whiteSpace: 'nowrap' }}>Lọc trạng thái:</span>
                                        <Form.Control 
                                            as="select" 
                                            size="sm" 
                                            style={{ width: '150px' }}
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="ALL">Tất cả</option>
                                            <option value="0">Cần làm</option>
                                            <option value="1">Đang làm</option>
                                            <option value="2">Chờ duyệt</option>
                                            <option value="3">Hoàn thành</option>
                                            <option value="4">Quá hạn</option>
                                        </Form.Control>
                                    </div>
                                </div>
                                <Accordion defaultActiveKey="0" className="mb-4">
                                    {trackingData.annualJobRegistrations.map((reg, index) => {
                                        const filteredDetails = statusFilter === 'ALL' 
                                            ? reg.details 
                                            : reg.details.filter(d => d.status.toString() === statusFilter);

                                        if (statusFilter !== 'ALL' && filteredDetails.length === 0) {
                                            return null;
                                        }

                                        return (
                                            <Card key={reg.id || index} className="mb-2 border">
                                                <Accordion.Toggle as={Card.Header} eventKey={index.toString()} style={{ cursor: 'pointer' }} className="bg-light">
                                                    <div className="d-flex w-100 justify-content-between align-items-center">
                                                        <h6 className="fw-bold text-success mb-0">
                                                            <i className="bi bi-calendar-check me-2"></i>
                                                            Phiếu: {reg.name}
                                                        </h6>
                                                        <span className="text-muted small">
                                                            <i className="bi bi-clock me-1"></i>
                                                            {moment(reg.startDate).format('DD/MM/YYYY')} - {moment(reg.endDate).format('DD/MM/YYYY')}
                                                        </span>
                                                    </div>
                                                </Accordion.Toggle>
                                                <Accordion.Collapse eventKey={index.toString()}>
                                                    <Card.Body className="p-3">
                                                        <div className="table-responsive">
                                                            <Table bordered hover size="sm" className="bg-white mb-0">
                                                                <thead>
                                                                    <tr>
                                                                        <th>STT</th>
                                                                        <th>Công việc thường niên</th>
                                                                        <th>Đăng ký (KPI)</th>
                                                                        <th>Đã đạt (KPI)</th>
                                                                        <th>Điểm quy đổi</th>
                                                                        <th>Trạng thái</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {filteredDetails.map((d, i) => {
                                                                        const statusMap = {
                                                                            0: { bg: 'secondary', text: 'Cần làm' },
                                                                            1: { bg: 'primary', text: 'Đang làm' },
                                                                            2: { bg: 'info', text: 'Chờ duyệt' },
                                                                            3: { bg: 'success', text: 'Hoàn thành' },
                                                                            4: { bg: 'danger', text: 'Quá hạn' },
                                                                        };
                                                                        const st = statusMap[d.status] || { bg: 'dark', text: 'Không rõ' };
                                                                        return (
                                                                            <tr key={d.id}>
                                                                                <td>{i+1}</td>
                                                                                <td>{d.annualJobName}</td>
                                                                                <td>{d.registeredKPI} {d.annualJobUnit}</td>
                                                                                <td className="fw-bold text-primary">{d.currentKPI} {d.annualJobUnit}</td>
                                                                                <td className="fw-bold text-success">{d.currentScore.toFixed(2)}</td>
                                                                                <td><Badge variant={st.bg}>{st.text}</Badge></td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </Card.Body>
                                                </Accordion.Collapse>
                                            </Card>
                                        );
                                    })}
                                </Accordion>
                            </>
                        ) : (
                            <div className="alert alert-warning">Người dùng không có Phiếu đăng ký thường niên nào đang hoạt động.</div>
                        )}

                        <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
                            <h6 className="mb-0 fw-bold text-primary">
                                <i className="bi bi-lightning-charge me-2"></i>
                                Dự án / Công việc đột xuất tham gia cùng thời kỳ
                            </h6>
                            <div className="d-flex align-items-center">
                                <span className="me-2 text-muted small whitespace-nowrap" style={{ whiteSpace: 'nowrap' }}>Lọc trạng thái:</span>
                                <Form.Control 
                                    as="select" 
                                    size="sm" 
                                    style={{ width: '150px' }}
                                    value={projectStatusFilter}
                                    onChange={(e) => setProjectStatusFilter(e.target.value)}
                                >
                                    <option value="ALL">Tất cả</option>
                                    <option value="0">Đang thực hiện</option>
                                    <option value="2">Hoàn thành</option>
                                    <option value="OTHER">Khác</option>
                                </Form.Control>
                            </div>
                        </div>

                        {(() => {
                            const filteredProjects = (!trackingData.activeProjects) ? [] : 
                                projectStatusFilter === 'ALL' ? trackingData.activeProjects :
                                trackingData.activeProjects.filter(p => {
                                    if (projectStatusFilter === '0') return p.status === 0;
                                    if (projectStatusFilter === '2') return p.status === 2;
                                    return p.status !== 0 && p.status !== 2;
                                });

                            if (filteredProjects.length === 0) {
                                return (
                                    <div className="alert alert-warning">Người dùng không có Dự án / Công việc đột xuất nào phù hợp.</div>
                                );
                            }

                            return (
                                <Accordion defaultActiveKey="0" className="mb-4">
                                    {filteredProjects.map((p, index) => (
                                        <Card key={p.id || index} className="mb-2 border">
                                            <Accordion.Toggle as={Card.Header} eventKey={index.toString()} style={{ cursor: 'pointer' }} className="bg-light">
                                                <div className="d-flex w-100 justify-content-between align-items-center">
                                                    <h6 className="fw-bold text-primary mb-0">
                                                        <i className="bi bi-lightning-charge me-2"></i>
                                                        Dự án: {p.name}
                                                    </h6>
                                                    <span className="text-muted small">
                                                        <i className="bi bi-clock me-1"></i>
                                                        {moment(p.startDate).format('DD/MM/YYYY')} - {moment(p.endDate).format('DD/MM/YYYY')}
                                                    </span>
                                                </div>
                                            </Accordion.Toggle>
                                            <Accordion.Collapse eventKey={index.toString()}>
                                                <Card.Body className="p-3">
                                                    <div className="table-responsive">
                                                        <Table bordered hover size="sm" className="bg-white mb-0">
                                                            <thead>
                                                                <tr>
                                                                    <th>Vai trò</th>
                                                                    <th>Từ ngày</th>
                                                                    <th>Đến ngày</th>
                                                                    <th>Điểm KPI đột xuất</th>
                                                                    <th>Trạng thái DA</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td>Thành viên</td>
                                                                    <td>{moment(p.startDate).format('DD/MM/YYYY')}</td>
                                                                    <td>{moment(p.endDate).format('DD/MM/YYYY')}</td>
                                                                    <td className="fw-bold text-warning">{p.memberWeight ? p.memberWeight.toFixed(2) : '0.00'}</td>
                                                                    <td>
                                                                        {p.status === 2 ? <Badge variant="success">Hoàn thành</Badge> : 
                                                                         p.status === 0 ? <Badge variant="primary">Đang thực hiện</Badge> : 
                                                                         <Badge variant="secondary">Khác</Badge>}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </Card.Body>
                                            </Accordion.Collapse>
                                        </Card>
                                    ))}
                                </Accordion>
                            );
                        })()}
                    </>
                )}
                </Card.Body>
        </Card>
        </div>
    );
};

export default MemberTracking;
